import { Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogTitle } from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { DialogComponent } from '../../../../../core/models/dialog.data';
import {
  ClientAccount,
  ExecutiveReportConfig,
  HorizonScan,
  NessusScan,
  NessusScanHistory,
  ReportConfig,
  ReportLanguage,
  ReportModuleConfig,
} from '../../../../../core/models';
import { AdminScanService, AuthService } from '../../../../../core/services';
import { ClientAccountStore, HorizonScanStore, NessusScanStore } from '../../../../../core/stores';
import { AdminReportType, LanguageEnum } from '../../../../../core/enums';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { startWith } from 'rxjs/operators';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckbox } from '@angular/material/checkbox';
import { AdminHorizonScanOptionComponent } from '../components/admin-horizon-scan-option/admin-horizon-scan-option.component';
import { AdminNessusScanHistoryOptionComponent } from '../components/admin-nessus-scan-history-option/admin-nessus-scan-history-option.component';
import { scoreCompanyNameMatches } from '../../../shared/utilities/score-company-name-matches';
import { MatDivider } from '@angular/material/list';
import { compareDesc } from 'date-fns';

@Component({
  selector: 'dbx-admin-generate-report-dialog',
  standalone: true,
  imports: [
    MatButton,
    TranslateModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatFormField,
    MatLabel,
    MatInput,
    ReactiveFormsModule,
    MatError,
    MatOption,
    MatSelect,
    MatProgressSpinner,
    MatAutocompleteModule,
    MatCheckbox,
    AdminHorizonScanOptionComponent,
    AdminNessusScanHistoryOptionComponent,
    MatDivider,
  ],
  providers: [HorizonScanStore, NessusScanStore],
  templateUrl: './admin-generate-report-dialog.component.html',
  styleUrl: './admin-generate-report-dialog.component.scss',
})
export class AdminGenerateReportDialogComponent extends DialogComponent<
  AdminGenerateReportDialogComponent,
  { reportType: AdminReportType },
  ReportConfig | ExecutiveReportConfig
> {
  private readonly data = inject<{ reportType: AdminReportType }>(MAT_DIALOG_DATA, {
    optional: true,
  });
  readonly reportType = this.data?.reportType ?? AdminReportType.Detailed;
  readonly AdminReportType = AdminReportType;

  private readonly formBuilder = inject(FormBuilder);
  private readonly adminScanService = inject(AdminScanService);
  private readonly authService = inject(AuthService);
  private readonly toastrService = inject(ToastrService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly clientAccountStore = inject(ClientAccountStore);
  private readonly horizonScanStore = inject(HorizonScanStore);
  private readonly nessusScanStore = inject(NessusScanStore);

  readonly internalScans = this.horizonScanStore.internalScans;
  readonly externalScans = this.horizonScanStore.externalScans;
  readonly isScansLoading = this.horizonScanStore.isLoading;

  readonly nessusScans = this.nessusScanStore.scans;
  readonly nessusHistory = this.nessusScanStore.history;
  readonly isNessusScansLoading = this.nessusScanStore.isScansLoading;
  readonly isNessusHistoryLoading = this.nessusScanStore.isHistoryLoading;

  readonly accounts = this.clientAccountStore.accounts;
  readonly clientAccounts = this.clientAccountStore.accounts;
  readonly filteredClients = computed(() => {
    const value = this.clientSearch();
    const accounts = this.clientAccounts();

    const search = this.getCompanyName(value);

    return search ? this.filterClients(search, accounts) : accounts;
  });

  languages = Object.values(ReportLanguage);
  isLoading = false;

  formGroup = this.formBuilder.group(
    {
      companyName: new FormControl<ClientAccount | string | null>(null, [
        Validators.required,
        this.clientObjectValidator,
      ]),
      scanName: new FormControl('', this.reportType === AdminReportType.Executive ? [] : [Validators.required]),
      language: new FormControl(this.mapLanguage(this.authService.getLanguage()), [Validators.required]),
      threshold: new FormControl(5.9, this.reportType === AdminReportType.Executive ? [] : [Validators.required]),
      isc: new FormControl(false),
      reducedConfiguration: this.formBuilder.group({
        numInternalFindings: new FormControl<number>(2, { nonNullable: true, validators: [Validators.required] }),
        numExternalFindings: new FormControl<number>(1, { nonNullable: true, validators: [Validators.required] }),
      }),
      horizonApiKey: new FormControl('', []),
      horizonInternal: this.formBuilder.group({
        enabled: [false],
        scan: new FormControl<HorizonScan | null>(null, [this.scanObjectValidator]),
        compareTo: new FormControl<HorizonScan | null>(null, [this.scanObjectValidator]),
      }),
      horizonExternal: this.formBuilder.group({
        enabled: [false],
        scan: new FormControl<HorizonScan | null>(null, [this.scanObjectValidator]),
        compareTo: new FormControl<HorizonScan | null>(null, [this.scanObjectValidator]),
      }),
      nessus: this.formBuilder.group({
        enabled: [false],
        scan: new FormControl<NessusScan | null>(null, [this.scanObjectValidator]),
        history: new FormControl<NessusScanHistory | null>(null, [this.scanObjectValidator]),
        compareTo: new FormControl<NessusScanHistory | null>(null, [this.scanObjectValidator]),
      }),
      breachLense: this.formBuilder.group({
        enabled: [false],
        domain: [''],
      }),
    },
    { validators: this.atLeastOneModuleEnabledValidator },
  );

  readonly clientSearch = toSignal<ClientAccount | string | null>(
    this.formGroup.get('companyName')!.valueChanges.pipe(startWith(this.formGroup.get('companyName')!.value)),
    { initialValue: null },
  );

  readonly client = toSignal(
    this.formGroup.get('companyName')!.valueChanges.pipe(startWith(this.formGroup.get('companyName')!.value)),
    { initialValue: null },
  );

  // Either module's horizonKey works — same key, watch internal as source of truth
  readonly horizonKey = toSignal(
    this.formGroup.get('horizonApiKey')!.valueChanges.pipe(startWith(this.formGroup.get('horizonApiKey')!.value)),
    { initialValue: '' },
  );

  readonly internalScanSearch = toSignal(this.formGroup.get('horizonInternal.scan')!.valueChanges.pipe(startWith('')), {
    initialValue: '',
  });

  readonly externalScanSearch = toSignal(this.formGroup.get('horizonExternal.scan')!.valueChanges.pipe(startWith('')), {
    initialValue: '',
  });

  readonly filteredInternalScans = computed(() => {
    const value = this.internalScanSearch();
    const scans = this.internalScans();
    const search = typeof value === 'string' ? value : ((value as unknown as HorizonScan)?.name ?? '');
    return search ? this.filterScans(search, scans) : scans;
  });
  private formSignal = toSignal(this.formGroup.valueChanges.pipe(startWith(this.formGroup.value)), {
    initialValue: this.formGroup.value,
  });

  readonly comparableInternalScans = computed(() => {
    const targetScan = this.formSignal().horizonInternal?.scan;
    const scans = this.filteredInternalScans();
    const search = targetScan?.opId;
    return search ? scans.filter((scan) => scan.opId !== search) : scans;
  });

  readonly filteredExternalScans = computed(() => {
    const value = this.externalScanSearch();
    const scans = this.externalScans();
    const search = typeof value === 'string' ? value : ((value as unknown as HorizonScan)?.name ?? '');
    return search ? this.filterScans(search, scans) : scans;
  });

  readonly comparableExternalScans = computed(() => {
    const targetScan = this.formSignal().horizonExternal?.scan;
    const scans = this.filteredExternalScans();
    const search = targetScan?.opId;
    return search ? scans.filter((scan) => scan.opId !== search) : scans;
  });

  sortedNessusConfigs = computed(() => {
    const company = this.formSignal().companyName;
    const companyName = this.getCompanyName(company);
    if (!companyName) {
      return {
        likely: this.nessusScans().sort((a, b) => a.name.localeCompare(b.name)),
        unlikely: [],
      };
    }

    const scoredConfigs = scoreCompanyNameMatches(companyName, this.nessusScans(), {
      getValue: (config) => config.name,
      symmetricContainment: false,
    }).sort((a, b) => b.score - a.score);

    const threshold = 0.4;
    const likely = scoredConfigs.filter((sc) => sc.score >= threshold).map((sc) => sc.item);

    const unlikely = scoredConfigs
      .filter((sc) => sc.score < threshold)
      .map((sc) => sc.item)
      .sort((a, b) => a.name.localeCompare(b.name));

    return { likely, unlikely };
  });

  readonly sortedNessusHistory = computed(() => {
    return this.nessusHistory().sort((a, b) => compareDesc(a.creationDate, b.creationDate));
  });

  readonly nessusHistorySearch = toSignal(this.formGroup.get('nessus.history')!.valueChanges.pipe(startWith('')), {
    initialValue: '',
  });

  readonly filteredNessusHistory = computed(() => {
    const value = this.nessusHistorySearch();
    const history = this.sortedNessusHistory();
    const search = typeof value === 'string' ? value : ((value as unknown as NessusScanHistory)?.historyId ?? '');
    return search ? history.filter((h) => h.historyId.toLowerCase().includes(search.toLowerCase())) : history;
  });

  readonly comparableNessusHistory = computed(() => {
    const historyEntry = this.formSignal().nessus?.history;
    const scans = this.filteredNessusHistory();
    const search = historyEntry?.historyId;
    return search ? scans.filter((scan) => scan.historyId !== search) : scans;
  });

  readonly debouncedHorizonKey = signal('');

  constructor() {
    super();
    this.clientAccountStore.loadAccounts();
    this.setupValidation('horizonInternal', ['scan']);
    this.setupValidation('horizonExternal', ['scan']);
    this.setupValidation('nessus', ['scan', 'history']);
    this.setupValidation('breachLense', ['domain']);

    this.nessusScanStore
      .loadScans()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (scans) => this.nessusScanStore.setScans(scans),
        error: () => this.nessusScanStore.clear(),
      });

    // Load nessus history when scan selection changes
    this.formGroup
      .get('nessus.scan')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const scan = value as NessusScan | null;
        this.nessusScanStore.clearHistory();
        this.formGroup.get('nessus.history')!.setValue(null);
        if (scan?.id) {
          this.nessusScanStore
            .loadHistory(scan.id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (history) => this.nessusScanStore.setHistory(history),
              error: () => this.nessusScanStore.clearHistory(),
            });
        }
      });

    // Debounce the horizon key before triggering a load
    effect((onCleanup) => {
      const value = this.horizonKey();

      const timer = setTimeout(() => {
        this.debouncedHorizonKey.set(value ?? '');
      }, 2500);

      onCleanup(() => clearTimeout(timer));
    });

    // Load scans when debounced key + company are both present
    effect((onCleanup) => {
      const apiKey = this.debouncedHorizonKey();
      const clientName = this.getCompanyName(this.client());

      if (!apiKey || !clientName) {
        this.horizonScanStore.clear();
        return;
      }

      const apiKeyControl = this.formGroup.get('horizonApiKey');

      if (apiKeyControl?.hasError('invalidApiKey')) {
        const errors = { ...apiKeyControl.errors };
        delete errors['invalidApiKey'];
        apiKeyControl.setErrors(Object.keys(errors).length ? errors : null);
      }

      const subscription = this.horizonScanStore.loadScans(apiKey, clientName).subscribe({
        next: (scans) => {
          this.horizonScanStore.setScans(scans);
        },

        error: (err) => {
          if (err.status === 403) {
            apiKeyControl?.setErrors({ invalidApiKey: true });
          }
          this.horizonScanStore.clear();
        },
      });

      onCleanup(() => subscription.unsubscribe());
    });
  }

  private mapLanguage(language: LanguageEnum): ReportLanguage {
    switch (language) {
      case LanguageEnum.German:
        return ReportLanguage.GERMAN;
      default:
        return ReportLanguage.ENGLISH;
    }
  }

  displayClientFn = (client: ClientAccount | string | null): string => {
    return this.getCompanyName(client);
  };

  private getCompanyName(value: ClientAccount | string | null | undefined): string {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value.companyName;
  }

  private filterClients(name: string, accounts: ClientAccount[]): ClientAccount[] {
    const filterValue = name.toLowerCase();
    return accounts.filter((client) => client.companyName.toLowerCase().includes(filterValue));
  }

  private filterScans(value: string, scans: HorizonScan[]): HorizonScan[] {
    const filterValue = value.toLowerCase();
    return scans.filter((scan) => scan.name.toLowerCase().includes(filterValue));
  }

  private dateToString(date: Date): string {
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  }

  displayScanFn = (scan: HorizonScan): string => {
    if (!scan) return '';
    const startDate = this.dateToString(scan.launchedAt);
    const endDate = scan.completedAt ? ` - ${this.dateToString(scan.completedAt)}` : '';
    return `${scan.name} (${startDate}${endDate})`;
  };

  displayNessusScanFn = (scan: NessusScan): string => {
    return scan ? scan.name : '';
  };

  displayNessusHistoryFn(history: NessusScanHistory): string {
    return history ? history.historyId : '';
  }

  scanObjectValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value && typeof value === 'string') {
      return { invalidScan: true };
    }
    return null;
  }

  private setupValidation(groupName: string, fields: string[]) {
    this.formGroup
      .get(`${groupName}.enabled`)
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((enabled) => {
        const group = this.formGroup.get(groupName);
        fields.forEach((field) => {
          const control = group?.get(field);
          if (enabled) {
            control?.setValidators([Validators.required]);
          } else {
            control?.clearValidators();
          }
          control?.updateValueAndValidity();
        });
      });
  }

  atLeastOneModuleEnabledValidator(control: AbstractControl): ValidationErrors | null {
    const horizonInternal = control.get('horizonInternal.enabled')?.value;
    const horizonExternal = control.get('horizonExternal.enabled')?.value;
    if (!horizonInternal && !horizonExternal) {
      return { noModuleEnabled: true };
    }
    return null;
  }

  clientObjectValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value && typeof value === 'string') {
      return { invalidClient: true };
    }
    return null;
  }

  save() {
    this.formGroup.markAllAsTouched();
    if (this.formGroup.valid) {
      this.isLoading = true;
      let call;
      if (this.reportType === AdminReportType.Executive) {
        call = this.adminScanService.generateExecutiveReport(this.mapToExecutiveRequest());
      } else if (this.reportType === AdminReportType.Full) {
        call = this.adminScanService.generateFullReport(this.mapToRequest() as ReportConfig);
      } else {
        call = this.adminScanService.generateReport(this.mapToRequest() as ReportConfig);
      }

      call.pipe(finalize(() => (this.isLoading = false))).subscribe({
        next: () => this.close(),
        error: (err) => {
          this.toastrService.error(this.translateService.instant('admin.scans.alerts.generateReportFail'));
        },
      });
    }
  }

  mapToExecutiveRequest(): ExecutiveReportConfig {
    const rawValue = this.formGroup.getRawValue();
    const modules: ReportModuleConfig[] = [];

    if (rawValue.horizonInternal?.enabled) {
      const scan = rawValue.horizonInternal.scan as unknown as HorizonScan;
      modules.push({
        type: 'Horizon3 NodeZero',
        subtype: 'internal',
        horizonKey: rawValue.horizonApiKey || '',
        scanId: scan?.opId || '',
      });
    }
    if (rawValue.horizonExternal?.enabled) {
      const scan = rawValue.horizonExternal.scan as unknown as HorizonScan;
      modules.push({
        type: 'Horizon3 NodeZero',
        subtype: 'external',
        horizonKey: rawValue.horizonApiKey || '',
        scanId: scan?.opId || '',
      });
    }
    if (rawValue.nessus?.enabled) {
      const scan = rawValue.nessus.scan as unknown as NessusScan;
      const history = rawValue.nessus.history as unknown as NessusScanHistory;
      modules.push({
        type: 'Tenable Nessus',
        scanId: scan?.id || '',
        historyId: history?.historyId,
      });
    }

    return {
      companyName: this.getCompanyName(rawValue.companyName),
      language: rawValue.language!!,
      modules: modules,
    };
  }

  mapToRequest(): ReportConfig {
    const rawValue = this.formGroup.getRawValue();
    const modules: ReportModuleConfig[] = [];

    if (rawValue.horizonInternal?.enabled) {
      const scan = rawValue.horizonInternal.scan as unknown as HorizonScan;
      modules.push({
        type: 'Horizon3 NodeZero',
        subtype: 'internal',
        horizonKey: rawValue.horizonApiKey || '',
        scanId: scan?.opId || '',
        compareTo: rawValue.horizonInternal.compareTo?.opId,
      });
    }
    if (rawValue.horizonExternal?.enabled) {
      const scan = rawValue.horizonExternal.scan as unknown as HorizonScan;
      modules.push({
        type: 'Horizon3 NodeZero',
        subtype: 'external',
        horizonKey: rawValue.horizonApiKey || '',
        scanId: scan?.opId || '',
        compareTo: rawValue.horizonExternal.compareTo?.opId,
      });
    }
    if (rawValue.nessus?.enabled) {
      const scan = rawValue.nessus.scan as unknown as NessusScan;
      const history = rawValue.nessus.history as unknown as NessusScanHistory;
      modules.push({
        type: 'Tenable Nessus',
        scanId: scan?.id || '',
        historyId: history?.historyId,
        compareTo: rawValue.nessus?.compareTo?.historyId,
      });
    }
    if (rawValue.breachLense?.enabled) {
      modules.push({
        type: 'Breachlense',
        domain: rawValue.breachLense.domain || '',
      });
    }

    return {
      companyName: this.getCompanyName(rawValue.companyName),
      scanName: rawValue.scanName!!,
      language: rawValue.language!!,
      threshold: rawValue.isc == true ? 0.1 : rawValue.threshold!!,
      reduced: rawValue.isc || false,
      reducedConfiguration: rawValue.reducedConfiguration,
      modules: modules,
    };
  }

  protected readonly JSON = JSON;
}
